import { FC, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faCalendar, faCircleExclamation, faCircleInfo, faCopy, faPlus, faTrash, faUsers, faUtensils } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { dateFormat, showError } from 'utils/utils';
import withErrorBoundary from '../../withErrorBoundary';
import { MyOrder, Pax } from 'ts/shared/interfaces/MOResponse';
import { changeSegmentEditor } from 'services/myOrders';
import { MoSegmentEditorOtherResponse } from 'ts/shared/interfaces/MOResponse';
import ConfirmDialog from '../../ConfirmDialog';
import { useTranslation } from 'react-i18next';
import Tabs from 'components/ui/Tabs/Tabs';
interface MoRoomsTabProps {
  myOrder?: MyOrder;
  orderModel: any;
  setOrderModel: (value: any) => void;
  paxesModel: Pax[];
  roomsModel: MyOrder['fullSegment']['rooms'];
  setRoomsModel: (value: MyOrder['fullSegment']['rooms']) => void;
  activeRoomIndex: number;
  setActiveRoomIndex: (index: number) => void;
  segmentEditorOrder?: MoSegmentEditorOtherResponse;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  syncFromResult: (result: MyOrder) => void;
  onSubmit: (myOrder: MyOrder) => void;
}

const MoRoomsTab: FC<MoRoomsTabProps> = ({
  myOrder,
  orderModel,
  setOrderModel,
  paxesModel,
  roomsModel,
  setRoomsModel,
  activeRoomIndex,
  setActiveRoomIndex,
  segmentEditorOrder,
  isLoading,
  setIsLoading,
  syncFromResult,
  onSubmit,
}) => {
  const [t] = useTranslation();
  const [activeRoomTab, setActiveRoomTab] = useState(0);
  const [showConfirmCopyRoom, setShowConfirmCopyRoom] = useState(false);
  const [showConfirmDeleteRoom, setShowConfirmDeleteRoom] = useState(false);
  const [copyRoomIndex, setCopyRoomIndex] = useState<number | null>(null);
  const [deleteRoomIndex, setDeleteRoomIndex] = useState<number | null>(null);
  const [loadingNewRoom, setLoadingNewRoom] = useState(false);

  const statusMap = segmentEditorOrder?.activeStatus || segmentEditorOrder?.allStatus || {};
  const roomStatusOptions: Select[] = Object.entries(statusMap).map(([code, desc]) => ({
    value: code,
    label: `[${code}] ${desc}`,
  }));
  const roomCodeOptions: Select[] = [
    ...(segmentEditorOrder?.regularRooms || []),
    ...(segmentEditorOrder?.apartmentRooms || []),
    ...(segmentEditorOrder?.occupancyRooms || []),
  ].reduce<Select[]>((acc, room) => {
    if (acc.some(option => option.value === room.sysRoomCode)) return acc;
    return [...acc, { value: room.sysRoomCode, label: `${room.sysRoomCode} - ${room.sysRoomDesc}` }];
  }, []);
  const visibleRoomsWithIndex = roomsModel
    .map((room, realIndex) => ({ room, realIndex }))
    .filter(({ room }) => (room.delRow || 0) === 0);
  const visibleRoomCount = visibleRoomsWithIndex.length;
  const selectedRoom = roomsModel[activeRoomIndex];
  const allPaxes = (orderModel?.paxes || paxesModel).filter((pax: Pax) => (pax.delRow || 0) === 0);
  const activeRoomPaxCodes = selectedRoom?.paxCodes || [];
  const activeRoomPaxes = allPaxes.filter((pax: Pax) => activeRoomPaxCodes.includes(pax.paxCode));
  const assignedPaxCodes = new Set(
    visibleRoomsWithIndex.flatMap(({ room }) => room.paxCodes || []),
  );
  const paxesWithoutRoom = allPaxes.filter((pax: Pax) => !assignedPaxCodes.has(pax.paxCode));

  const submitResult = (result: MyOrder) => {
    if (!myOrder) return;
    onSubmit({ ...result, orderRow: { ...result.orderRow, status: myOrder.orderRow.status } });
  };

  const onChangeSelectedRoom = (
    updater: (room: MyOrder['fullSegment']['rooms'][number]) => MyOrder['fullSegment']['rooms'][number],
  ) => {
    const newRooms = roomsModel.map((room, index) => (
      index === activeRoomIndex ? updater(room) : room
    ));
    setRoomsModel(newRooms);
    setOrderModel((prevOrder: any) => {
      if (!prevOrder || !myOrder) return prevOrder;
      return {
        ...prevOrder,
        segments: prevOrder.segments.map((s: any) => (
          s.orderSegId === myOrder.fullSegment.orderSegId ? { ...s, rooms: newRooms } : s
        )),
      };
    });
  };

  const buildOrderWithRooms = (order: any, newRooms: any[]) => {
    if (!order || !myOrder) return order;
    return {
      ...order,
      segments: order.segments.map((s: any) => (
        s.orderSegId === myOrder.fullSegment.orderSegId ? { ...s, rooms: newRooms } : s
      )),
    };
  };

  const persistRoomsChange = async (newRooms: any[]) => {
    if (!myOrder || !orderModel || isLoading) return;
    const oldRooms = roomsModel;
    const oldOrder = orderModel;
    const nextOrder = buildOrderWithRooms(orderModel, newRooms);

    setRoomsModel(newRooms);
    setOrderModel(nextOrder);
    setIsLoading(true);
    const result = await changeSegmentEditor({
      order: nextOrder,
      changePrice: true,
    });
    if (result) {
      syncFromResult(result);
      submitResult(result);
    } else {
      setRoomsModel(oldRooms);
      setOrderModel(oldOrder);
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  const onAddPaxToRoomAtIndex = async (roomIndex: number, paxCode: number) => {
    if (isLoading) return;
    const newRooms = roomsModel.map((room, index) => {
      if (index !== roomIndex) return room;
      const nextPaxCodes = room.paxCodes?.includes(paxCode) ? room.paxCodes : [...(room.paxCodes || []), paxCode];
      return {
        ...room,
        paxCodes: nextPaxCodes,
        paxes: allPaxes.filter((pax: Pax) => nextPaxCodes.includes(pax.paxCode)),
      };
    });
    await persistRoomsChange(newRooms);
  };

  const onRemovePaxFromRoomAtIndex = async (roomIndex: number, paxCode: number) => {
    if (isLoading) return;
    const newRooms = roomsModel.map((room, index) => {
      if (index !== roomIndex) return room;
      const nextPaxCodes = (room.paxCodes || []).filter(code => code !== paxCode);
      return {
        ...room,
        paxCodes: nextPaxCodes,
        paxes: allPaxes.filter((pax: Pax) => nextPaxCodes.includes(pax.paxCode)),
        roomPaxString: nextPaxCodes.join(','),

      };
    });
    await persistRoomsChange(newRooms);
  };

  const addNewRoom = async () => {
    if (!myOrder || !orderModel || loadingNewRoom) return;
    const templateRoom = roomsModel.find((room) => (room.delRow || 0) === 0);
    if (!templateRoom) {
      showError(t('something_went_wrong'));
      return;
    }
    const { orderSegDetailId, roomId, id, paxCodes, ...roomWithoutId } = templateRoom as any;
    const newRooms = [...roomsModel, { ...roomWithoutId, paxCodes: [], paxes: [], delRow: 0, roomPaxString: '', sysRoomCode: roomCodeOptions[0].value, suppRoomClass: { desc: '', descLoc: '', code: '' }, sysRoomClass: { desc: '', descLoc: '', code: '' } }];
    const oldRooms = roomsModel;
    const oldOrder = orderModel;
    const nextOrder = buildOrderWithRooms(orderModel, newRooms);

    setLoadingNewRoom(true);
    setRoomsModel(newRooms);
    setOrderModel(nextOrder);

    const result = await changeSegmentEditor({ order: nextOrder, changePrice: true });
    if (result) {
      syncFromResult(result);
      submitResult(result);
    } else {
      setRoomsModel(oldRooms);
      setOrderModel(oldOrder);
      showError(t('something_went_wrong'));
    }
    setActiveRoomIndex(newRooms.length - 1);
    setLoadingNewRoom(false);
  };

  const onClickSubmitCopyRoom = async () => {
    if (!myOrder || !orderModel) return;
    const sourceIndex = copyRoomIndex ?? activeRoomIndex;
    const sourceRoom = roomsModel[sourceIndex];
    if (!sourceRoom) return;

    const { orderSegDetailId, roomId, id, paxCodes, ...roomWithoutId } = sourceRoom as any;
    const newRooms = [...roomsModel, { ...roomWithoutId, paxCodes: [], paxes: [], delRow: 0, roomPaxString: '' }];
    const oldRooms = roomsModel;
    const oldOrder = orderModel;
    const nextOrder = buildOrderWithRooms(orderModel, newRooms);

    setRoomsModel(newRooms);
    setOrderModel(nextOrder);
    setActiveRoomIndex(newRooms.length - 1);
    setShowConfirmCopyRoom(false);
    setCopyRoomIndex(null);
    setIsLoading(true);

    const result = await changeSegmentEditor({
      order: nextOrder,
      changePrice: true,
    });

    if (result) {
      syncFromResult(result);
      submitResult(result);
    } else {
      setRoomsModel(oldRooms);
      setOrderModel(oldOrder);
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  const onClickSubmitDeleteRoom = async () => {
    if (!myOrder || !orderModel) return;
    if (visibleRoomCount <= 1) return;
    const sourceIndex = deleteRoomIndex ?? activeRoomIndex;
    if (sourceIndex < 0 || sourceIndex >= roomsModel.length) return;

    const newRooms = roomsModel.map((room, index) => (
      index === sourceIndex ? { ...room, delRow: 1 } : room
    ));
    const oldRooms = roomsModel;
    const oldOrder = orderModel;
    const nextOrder = buildOrderWithRooms(orderModel, newRooms);
    const nextVisibleRoomIndex = newRooms.findIndex((room: any) => (room.delRow || 0) === 0);
    const nextActiveIndex = nextVisibleRoomIndex >= 0 ? nextVisibleRoomIndex : 0;

    setRoomsModel(newRooms);
    setOrderModel(nextOrder);
    setActiveRoomIndex(nextActiveIndex);
    setShowConfirmDeleteRoom(false);
    setDeleteRoomIndex(null);
    setIsLoading(true);

    const result = await changeSegmentEditor({
      order: nextOrder,
      changePrice: true,
    });

    if (result) {
      syncFromResult(result);
      submitResult(result);
    } else {
      setRoomsModel(oldRooms);
      setOrderModel(oldOrder);
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  return (
    <div>
      <div className="mo-block mt-10">
        <div className="mo-block-title">Rooms</div>
        <div className="mo-block-content flex-column">
          <div className="ml-a mb-10">
            <Button
              title={'Add new room'}
              onClick={() => addNewRoom()}
              className="btn btn-default btn-sm"
              icon={faPlus}
              disabled={loadingNewRoom}
              iconclassname="mr-5"
            />
          </div>
          <div className="table-default table-small mb-10 text-sm">
            <table className="table-bordered-lines table-mobile">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Room Id</th>
                  <th>Order Id</th>
                  <th>Segment Id</th>
                  <th></th>
                  <th>Room Display</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRoomsWithIndex.map(({ room, realIndex }, index) => (
                  <tr
                    key={room.orderSegDetailId || realIndex}
                    className={activeRoomIndex === realIndex ? 'active' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setActiveRoomIndex(realIndex)}
                  >
                    <td>{index + 1}</td>
                    <td>{room.orderSegDetailId}</td>
                    <td>{room.orderId}</td>
                    <td>{room.orderSegId}</td>
                    <td>{room.numPax !== room.paxCodes?.length ? <Tooltip content={'Occupancy missmatch with room'}>
                      <FontAwesomeIcon icon={faCircleExclamation} color="var(--color-danger)" />
                    </Tooltip> : ''}</td>
                    <td>{room.roomDisplay}</td>
                    <td>
                      <Button
                        title={''}
                        icon={faCopy}
                        className="btn btn-default btn-sm mr-5"
                        onClick={() => {
                          setCopyRoomIndex(realIndex);
                          setShowConfirmCopyRoom(true);
                        }}
                      />
                      {visibleRoomCount > 1 && (
                        <Button
                          title={''}
                          icon={faTrash}
                          className="btn btn-danger btn-sm"
                          onClick={() => {
                            setDeleteRoomIndex(realIndex);
                            setShowConfirmDeleteRoom(true);
                          }}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div>
        <div className="mt-20">
          <Tabs
        size="small"
            label="Room"
            value={String(activeRoomTab)}
            onValueChange={(v) => setActiveRoomTab(Number(v))}
            className="mb-10 room-tabs"
            items={[
              { value: '9', label: <FontAwesomeIcon icon={faCircleInfo} /> },
              {
                value: '0',
                label: (
                  <>
                    <FontAwesomeIcon icon={faBed} className="mr-5" />
                    Status & Room
                  </>
                ),
              },
              {
                value: '1',
                label: (
                  <>
                    <FontAwesomeIcon icon={faCalendar} className="mr-5" />
                    Cancellation
                  </>
                ),
              },
              {
                value: '2',
                label: (
                  <>
                    <FontAwesomeIcon icon={faUtensils} className="mr-5" />
                    Meal & Supp.
                  </>
                ),
              },
              { value: '3', label: 'General' },
              {
                value: '4',
                label: (
                  <>
                    <FontAwesomeIcon icon={faUsers} className="mr-5" />
                    Paxes
                  </>
                ),
              },
            ]}
          />
          <div className="tab-content">
            <div className="tab-pane active">
              {activeRoomTab === 0 && (
                <div className="mo-block-content">
                  <div className="mo-block mb-20">
                    <div className="mo-block-title">
                      <span>Status</span>
                    </div>
                    <div className="mo-block-content">
                      <div className="row">
                        <div className="col-md-3 col-12 mb-15">
                          <div className="mt-10">Room Status</div>
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <SelectCustom
                            onChange={(value) => onChangeSelectedRoom(room => ({
                              ...room,
                              sysStatus: {
                                ...(room.sysStatus || { code: '', descLoc: '', desc: '' }),
                                code: value,
                                desc: statusMap[value as keyof typeof statusMap] || '',
                              },
                            }))}
                            value={selectedRoom?.sysStatus?.code || ''}
                            options={roomStatusOptions}
                            movablePlaceholder={'Room Status'}
                          />
                        </div>
                        <div className="col-md-3 col-12"></div>
                        <div className="col-md-3 col-6 mb-15">
                          <FormGroup
                            onChange={() => { }}
                            value={selectedRoom?.sysStatus?.code || ''}
                            label={'Sys Status Code'}
                            movablePlaceholder={'Sys Status Code'}
                            disabled={true}
                          />
                        </div>
                        <div className="col-md-3 col-6 mb-15">
                          <FormGroup
                            onChange={() => { }}
                            value={selectedRoom?.sysStatus?.desc || ''}
                            label={'Sys Status Desc'}
                            movablePlaceholder={'Sys Status Desc'}
                            disabled={true}
                          />
                        </div>
                        <div className="col-md-3 col-6 mb-15">
                          <FormGroup
                            onChange={() => { }}
                            value={selectedRoom?.suppStatus?.code || ''}
                            label={'Supp Status Code'}
                            movablePlaceholder={'Supp Status Code'}
                            disabled={true}
                          />
                        </div>
                        <div className="col-md-3 col-6 mb-15">
                          <FormGroup
                            onChange={() => { }}
                            value={selectedRoom?.suppStatus?.desc || ''}
                            label={'Supp Status Desc'}
                            movablePlaceholder={'Supp Status Desc'}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mo-block">
                    <div className="mo-block-title">
                      <span>Room</span>
                    </div>
                    <div className="mo-block-content">
                      <div className="row">
                        <div className="col-md-6 col-12 mb-15">
                          <SelectCustom
                            onChange={(value) => onChangeSelectedRoom(room => {
                              const selectedCode = value as string;
                              const selectedRoomMeta = [
                                ...(segmentEditorOrder?.regularRooms || []),
                                ...(segmentEditorOrder?.apartmentRooms || []),
                                ...(segmentEditorOrder?.occupancyRooms || []),
                              ].find(option => option.sysRoomCode === selectedCode);
                              return {
                                ...room,
                                sysRoomCode: selectedCode,
                                sysRoomDesc: selectedRoomMeta?.sysRoomDesc || room.sysRoomDesc,
                              };
                            })}
                            value={selectedRoom?.sysRoomCode || ''}
                            options={roomCodeOptions}
                            movablePlaceholder={'Sys Room Code'}
                          />
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, sysRoomDesc: value }))} value={selectedRoom?.sysRoomDesc || ''} label={'Sys Room Desc'} movablePlaceholder={'Sys Room Desc'} />
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <FormGroup
                            onChange={(value) => onChangeSelectedRoom(room => ({
                              ...room,
                              suppRoom: { ...(room.suppRoom || { code: '', descLoc: '', desc: '' }), code: value },
                            }))}
                            value={selectedRoom?.suppRoom?.code || ''}
                            label={'Supp Room Code'}
                            movablePlaceholder={'Supp Room Code'}
                          />
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <FormGroup
                            onChange={(value) => onChangeSelectedRoom(room => ({
                              ...room,
                              suppRoom: { ...(room.suppRoom || { code: '', descLoc: '', desc: '' }), desc: value },
                            }))}
                            value={selectedRoom?.suppRoom?.desc || ''}
                            label={'Supp Room Desc'}
                            movablePlaceholder={'Supp Room Desc'}
                          />
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <FormGroup
                            onChange={(value) => onChangeSelectedRoom(room => ({
                              ...room,
                              sysRoomClass: { ...(room.sysRoomClass || { code: '', descLoc: '', desc: '' }), code: value },
                            }))}
                            value={selectedRoom?.sysRoomClass?.code || ''}
                            label={'Sys Room Class Code'}
                            movablePlaceholder={'Sys Room Class Code'}
                          />
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <FormGroup
                            onChange={(value) => onChangeSelectedRoom(room => ({
                              ...room,
                              sysRoomClass: { ...(room.sysRoomClass || { code: '', descLoc: '', desc: '' }), desc: value },
                            }))}
                            value={selectedRoom?.sysRoomClass?.desc || ''}
                            label={'Sys Room Class Desc'}
                            movablePlaceholder={'Sys Room Class Desc'}
                          />
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <FormGroup
                            onChange={(value) => onChangeSelectedRoom(room => ({
                              ...room,
                              suppRoomClass: { ...(room.suppRoomClass || { code: '', descLoc: '', desc: '' }), code: value },
                            }))}
                            value={selectedRoom?.suppRoomClass?.code || ''}
                            label={'Supp Room Class Code'}
                            movablePlaceholder={'Supp Room Class Code'}
                          />
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <FormGroup
                            onChange={(value) => onChangeSelectedRoom(room => ({
                              ...room,
                              suppRoomClass: { ...(room.suppRoomClass || { code: '', descLoc: '', desc: '' }), desc: value },
                            }))}
                            value={selectedRoom?.suppRoomClass?.desc || ''}
                            label={'Supp Room Class Desc'}
                            movablePlaceholder={'Supp Room Class Desc'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeRoomTab === 9 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.numAdt?.toString() || ''} label={'Num Adt'} movablePlaceholder={'Num Adt'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.numCnn?.toString() || ''} label={'Num Cnn'} movablePlaceholder={'Num Cnn'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.numPax?.toString() || ''} label={'Num Pax'} movablePlaceholder={'Num Pax'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.numberOfCots?.toString() || ''} label={'Num of Cots'} movablePlaceholder={'Num of Cots'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.roomNumber?.toString() || ''} label={'Room Number'} movablePlaceholder={'Room Number'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.orderId?.toString() || ''} label={'Order Id'} movablePlaceholder={'Order Id'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.orderSegId?.toString() || ''} label={'Order Seg Id'} movablePlaceholder={'Order Seg Id'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.orderSegDetailId?.toString() || ''} label={'Order Seg Detail Id'} movablePlaceholder={'Order Seg Detail Id'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.userCodeC?.toString() || ''} label={'User Code C'} movablePlaceholder={'User Code C'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.userCodeU?.toString() || ''} label={'User Code U'} movablePlaceholder={'User Code U'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.dateC ? dateFormat(selectedRoom.dateC, 'dd/MM/yy HH:mm') : ''} label={'Date C'} movablePlaceholder={'Date C'} disabled={true} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.dateU ? dateFormat(selectedRoom.dateU, 'dd/MM/yy HH:mm') : ''} label={'Date U'} movablePlaceholder={'Date U'} disabled={true} />
                    </div>
                  </div>
                </div>
              )}
              {activeRoomTab === 1 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-12 col-12 mb-15">
                      <FormGroupDatepicker
                        onChange={(value) => onChangeSelectedRoom(room => ({ ...room, cancellationDateLimit: value.toISOString() }))}
                        value={new Date(selectedRoom?.cancellationDateLimit || '')}
                        label={'Cancellation Date'}
                        movablePlaceholder={'Cancellation Date'}
                        movablePlaceholderIcon={faCalendar}
                      />
                    </div>
                    <div className="col-md-12 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSelectedRoom(room => ({ ...room, cancellationTimeLimit: value }))}
                        value={selectedRoom?.cancellationTimeLimit || ''}
                        label={'Cancellation Time'}
                        movablePlaceholder={'Cancellation Time'}
                      />
                    </div>
                    <div className="col-md-12 col-12 mb-15">
                      <div className="mb-5">Cancellation Note</div>
                      <textarea
                        className="form-control form-control-textarea"
                        value={selectedRoom?.cancellationNote || ''}
                        rows={7}
                        onChange={(e) => onChangeSelectedRoom(room => ({ ...room, cancellationNote: e.target.value }))}
                      ></textarea>
                    </div>
                  </div>
                </div>
              )}
              {activeRoomTab === 2 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, sysBasisCode: value }))} value={selectedRoom?.sysBasisCode || ''} label={'Sys Basis Code'} movablePlaceholder={'Sys Basis Code'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, sysBasisDesc: value }))} value={selectedRoom?.sysBasisDesc || ''} label={'Sys Basis Desc'} movablePlaceholder={'Sys Basis Desc'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, basisCode: value }))} value={selectedRoom?.basisCode || ''} label={'Basis Code'} movablePlaceholder={'Basis Code'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, basisDesc: value }))} value={selectedRoom?.basisDesc || ''} label={'Basis Desc'} movablePlaceholder={'Basis Desc'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSelectedRoom(room => ({
                          ...room,
                          sysMeal: { ...(room.sysMeal || { code: '', descLoc: '', desc: '' }), code: value },
                        }))}
                        value={selectedRoom?.sysMeal?.code || ''}
                        label={'Sys Meal Code'}
                        movablePlaceholder={'Sys Meal Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSelectedRoom(room => ({
                          ...room,
                          sysMeal: { ...(room.sysMeal || { code: '', descLoc: '', desc: '' }), desc: value },
                        }))}
                        value={selectedRoom?.sysMeal?.desc || ''}
                        label={'Sys Meal Desc'}
                        movablePlaceholder={'Sys Meal Desc'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSelectedRoom(room => ({
                          ...room,
                          meal: { ...(room.meal || { code: '', descLoc: '', desc: '' }), code: value },
                        }))}
                        value={selectedRoom?.meal?.code || ''}
                        label={'Meal Code'}
                        movablePlaceholder={'Meal Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => onChangeSelectedRoom(room => ({
                          ...room,
                          meal: { ...(room.meal || { code: '', descLoc: '', desc: '' }), desc: value },
                        }))}
                        value={selectedRoom?.meal?.desc || ''}
                        label={'Meal Desc'}
                        movablePlaceholder={'Meal Desc'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, sysSupplimentCode: value }))} value={selectedRoom?.sysSupplimentCode || ''} label={'Sys Supp. Code'} movablePlaceholder={'Sys Supp. Code'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, sysSupplimentDesc: value }))} value={selectedRoom?.sysSupplimentDesc || ''} label={'Sys Supp. Desc'} movablePlaceholder={'Sys Supp. Desc'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, supplimentCode: value }))} value={selectedRoom?.supplimentCode || ''} label={'Suppliment Code'} movablePlaceholder={'Suppliment Code'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, supplimentDesc: value }))} value={selectedRoom?.supplimentDesc || ''} label={'Suppliment Desc'} movablePlaceholder={'Suppliment Desc'} />
                    </div>
                  </div>
                </div>
              )}
              {activeRoomTab === 3 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, cnnAge1: Number(value) || 0 }))} value={selectedRoom?.cnnAge1?.toString() || ''} label={'Cnn Age 1'} movablePlaceholder={'Cnn Age 1'} allowOnlyNumbers />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, cnnAge2: Number(value) || 0 }))} value={selectedRoom?.cnnAge2?.toString() || ''} label={'Cnn Age 2'} movablePlaceholder={'Cnn Age 2'} allowOnlyNumbers />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, extraBed: Number(value) || 0 }))} value={selectedRoom?.extraBed?.toString() || ''} label={'Extra Bed'} movablePlaceholder={'Extra Bed'} allowOnlyNumbers />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, shareBed: Number(value) || 0 }))} value={selectedRoom?.shareBed?.toString() || ''} label={'Share Bed'} movablePlaceholder={'Share Bed'} allowOnlyNumbers />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, dblBed: Number(value) || 0 }))} value={selectedRoom?.dblBed?.toString() || ''} label={'Dbl Bed'} movablePlaceholder={'Dbl Bed'} allowOnlyNumbers />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, nonRefund: Number(value) || 0 }))} value={selectedRoom?.nonRefund?.toString() || ''} label={'Non Refund'} movablePlaceholder={'Non Refund'} allowOnlyNumbers />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, suppRoomId: value }))} value={selectedRoom?.suppRoomId || ''} label={'Supp Room Id'} movablePlaceholder={'Supp Room Id'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, hotelRoomTypeId: value }))} value={selectedRoom?.hotelRoomTypeId || ''} label={'Room Type Id'} movablePlaceholder={'Room Type Id'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, suppKey: value }))} value={selectedRoom?.suppKey || ''} label={'Supp Key'} movablePlaceholder={'Supp Key'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, seqNum: Number(value) || 0 }))} value={selectedRoom?.seqNum?.toString() || ''} label={'Seq Num'} movablePlaceholder={'Seq Num'} allowOnlyNumbers />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, exteranlId_1: value }))} value={selectedRoom?.exteranlId_1 || ''} label={'ExteranlId 1'} movablePlaceholder={'ExteranlId 1'} />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup onChange={(value) => onChangeSelectedRoom(room => ({ ...room, exteranlId_2: value }))} value={selectedRoom?.exteranlId_2 || ''} label={'ExteranlId 2'} movablePlaceholder={'ExteranlId 2'} />
                    </div>
                    <div className="col-md-12 col-12 mb-15">
                      <FormGroup onChange={() => { }} value={selectedRoom?.bToken || ''} label={'BToken'} movablePlaceholder={'BToken'} disabled={true} />
                    </div>
                  </div>
                </div>
              )}
              {activeRoomTab === 4 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-6 col-12 mb-15">
                      <div className="mo-block mb-30">
                        <div className="mo-block-title">
                          {selectedRoom?.orderSegDetailId || ''} - {selectedRoom?.roomDisplay || ''}
                        </div>
                        <div className="mo-block-content">
                          {activeRoomPaxes.length > 0 && (
                            <div className="table-default table-small text-sm">
                              <table className="table-bordered-lines table-mobile">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Full Name</th>
                                    <th style={{ width: '100px' }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {activeRoomPaxes.map((pax: Pax, index: number) => (
                                    <tr key={pax.paxCode || index} style={{ height: 38 }}>
                                      <td>{index + 1}</td>
                                      <td>{`${pax.firstName || ''} ${pax.lastName || ''}`.trim()}</td>
                                      <td>
                                        <Button
                                          title={''}
                                          icon={faTrash}
                                          className="btn btn-danger btn-sm"
                                          onClick={() => onRemovePaxFromRoomAtIndex(activeRoomIndex, pax.paxCode)}
                                          disabled={isLoading}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {activeRoomPaxes.length === 0 && (
                            <div className="text-center mb-20 mt-20 text-light">
                              No Paxes in Room
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <div className="mo-block">
                        <div className="mo-block-title">
                          <span>Paxes To Add</span>
                        </div>
                        <div className="mo-block-content">
                          {paxesWithoutRoom.length === 0 && (
                            <div className="text-center mb-20 mt-20 text-light">
                              No Paxes To Add
                            </div>
                          )}
                          {paxesWithoutRoom.length > 0 && (
                            <div className="table-default table-small text-sm" style={{ overflowY: 'visible' }}>
                              <table className="table-bordered-lines table-mobile">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Full Name</th>
                                    <th style={{ width: '100px' }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paxesWithoutRoom.map((pax: Pax, index: number) => (
                                    <tr key={pax.paxCode || index}>
                                      <td>{pax.paxCode || index + 1}</td>
                                      <td>{`${pax.firstName || ''} ${pax.lastName || ''}`.trim()}</td>
                                      <td>
                                        <Button
                                          title={''}
                                          icon={faPlus}
                                          className="btn btn-default btn-sm"
                                          disabled={isLoading}
                                          onClick={() => onAddPaxToRoomAtIndex(activeRoomIndex, pax.paxCode)}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showConfirmCopyRoom && (
        <ConfirmDialog
          title="Copy Room"
          openModal={showConfirmCopyRoom}
          close={() => {
            setShowConfirmCopyRoom(false);
            setCopyRoomIndex(null);
          }}
          onConfirm={() => onClickSubmitCopyRoom()}
        />
      )}
      {showConfirmDeleteRoom && (
        <ConfirmDialog
          title="Delete Room"
          openModal={showConfirmDeleteRoom}
          close={() => {
            setShowConfirmDeleteRoom(false);
            setDeleteRoomIndex(null);
          }}
          onConfirm={() => onClickSubmitDeleteRoom()}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(MoRoomsTab, 'MoRoomsTab');
